import { DestroyRef, Injectable, inject, signal } from '@angular/core';

/**
 * Knows which open forms hold edits that were not saved, so leaving them is not silent:
 *  - a route change asks first (`unsavedChangesGuard`, dialog `LeaveConfirmDialog`),
 *  - a reload, tab close or address change asks through the browser (`beforeunload`).
 *
 * A form registers once with `track` (constructor or field initializer, it ends with the component), so the guard
 * does not need to know which component or child form is on screen.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private readonly checks = new Set<() => boolean>();
  private answer: ((leave: boolean) => void) | null = null;

  /** True while the dialog asks whether to leave. */
  readonly asking = signal(false);

  constructor() {
    window.addEventListener('beforeunload', (event) => {
      if (this.hasUnsaved()) {event.preventDefault();}
    });
  }

  /** Registers `isDirty` until the calling component is destroyed. Call in an injection context. */
  track(isDirty: () => boolean): void {
    this.checks.add(isDirty);
    inject(DestroyRef).onDestroy(() => this.checks.delete(isDirty));
  }

  hasUnsaved(): boolean {
    for (const isDirty of this.checks) {
      if (isDirty()) {return true;}
    }
    return false;
  }

  /** Asks whether to leave with unsaved edits. A second question while one is open is answered "stay". */
  confirmLeave(): Promise<boolean> {
    if (this.answer) {return Promise.resolve(false);}
    return new Promise<boolean>((resolve) => {
      this.answer = resolve;
      this.asking.set(true);
    });
  }

  /** The user's answer: `true` = discard and leave, `false` = stay on the page. */
  answerLeave(leave: boolean): void {
    const answer = this.answer;
    this.answer = null;
    this.asking.set(false);
    answer?.(leave);
  }
}
